import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, MoreVertical, AlertTriangle, Boxes, Wallet, ClipboardCheck, History, Undo2, ScanLine, Printer,
} from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatMinor } from '@/lib/quoteMoney';
import { pickCatalogName, pickLocalizedField } from '@/i18n/localizeItem';
import { parseQty } from '@/lib/stockQty';
import { StockScanFlow } from '@/components/quotes/StockScanFlow';
import { StockLabelsDialog } from '@/components/quotes/StockLabelsDialog';
import { showApiError } from '@/lib/apiErrors';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { PlanLockBadge } from '@/components/quotes/PlanLockBadge';

const API_URL = process.env.REACT_APP_API_URL;
const LOCALES = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };


export function QuoteStock() {
  const { t, i18n } = useTranslation('quotes');
  const { entitlement, refresh: refreshEntitlement } = useQuoteEntitlement();
  const limits = entitlement?.limits || {};
  const barcodeLocked = limits.barcode_scanning === false;
  const stockLimit = limits.stock_item_limit ?? null;
  const goUpgrade = () => window.location.assign('/quote-upgrade');
  const lang = (i18n.language || 'tr').split('-')[0];
  const locale = LOCALES[lang] || 'tr-TR';
  const fmtQty = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(Number(n) || 0);

  const [tab, setTab] = useState('stock');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [lowOnly, setLowOnly] = useState(false);

  // movement dialog: { item, type: 'in' | 'out' | 'return' }
  const [movement, setMovement] = useState(null);
  const [movementQty, setMovementQty] = useState('');
  const [movementNote, setMovementNote] = useState('');
  // correction dialog (sets the real counted quantity)
  const [correcting, setCorrecting] = useState(null);
  const [correctQty, setCorrectQty] = useState('');
  // settings dialog (low-stock level / stop tracking)
  const [settingsItem, setSettingsItem] = useState(null);
  const [barcode, setBarcode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [minStock, setMinStock] = useState('');
  const [saving, setSaving] = useState(false);

  // history tab
  const [history, setHistory] = useState([]);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // count tab
  const [countRows, setCountRows] = useState([]);
  const [countValues, setCountValues] = useState({});
  const [countQuery, setCountQuery] = useState('');
  const [countLoading, setCountLoading] = useState(false);

  const name = (row) => pickCatalogName(row, lang);
  const unit = (row) => pickLocalizedField(row, 'unit', lang);

  const loadStock = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/stock`, {
        params: { q: query || undefined, low_only: lowOnly || undefined, page_size: 1000 },
      });
      setItems(data.items || []);
      setSummary(data.summary || null);
      refreshEntitlement();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (item = historyItem) => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/stock/movements`, {
        params: { item_id: item?.id || undefined, page_size: 200 },
      });
      setHistory(data.items || []);
    } catch (err) {
      showApiError(err, t);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadCountRows = async () => {
    setCountLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/stock`, {
        params: { scope: 'all', page_size: 1000 },
      });
      setCountRows(data.items || []);
    } catch (err) {
      showApiError(err, t);
    } finally {
      setCountLoading(false);
    }
  };

  useEffect(() => {
    loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowOnly]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
    if (tab === 'count') loadCountRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ------------------------------------------------------------ actions
  const openMovement = (item, type) => {
    setMovement({ item, type });
    setMovementQty('');
    setMovementNote('');
  };

  const submitMovement = async () => {
    const qty = parseQty(movementQty);
    if (!qty || qty <= 0) {
      toast.error(t('stock.validation.qty'));
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/stock/movements`, {
        item_id: movement.item.id,
        type: movement.type,
        qty,
        note: movementNote || undefined,
      });
      if (data.went_negative) toast.warning(t('stock.negativeWarning', { name: name(movement.item) }));
      else toast.success(t('common.saved'));
      setMovement(null);
      loadStock();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const submitCorrection = async () => {
    const qty = parseQty(correctQty);
    if (qty === null || qty < 0) {
      toast.error(t('stock.validation.qtyZero'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/quote/stock/count`, {
        entries: [{ item_id: correcting.id, qty }],
        note: t('stock.correctionNote'),
      });
      toast.success(t('common.saved'));
      setCorrecting(null);
      loadStock();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    const level = minStock === '' ? null : parseQty(minStock);
    if (minStock !== '' && (level === null || level < 0)) {
      toast.error(t('stock.validation.qtyZero'));
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/quote/stock/items/${settingsItem.id}`, { min_stock: level, ...(barcodeLocked ? {} : { barcode: barcode.trim() }) });
      toast.success(t('common.saved'));
      setSettingsItem(null);
      loadStock();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const stopTracking = async (item) => {
    if (!window.confirm(t('stock.stopTrackingConfirm', { name: name(item) }))) return;
    try {
      await axios.patch(`${API_URL}/api/quote/stock/items/${item.id}`, { track_stock: false });
      toast.success(t('common.saved'));
      setSettingsItem(null);
      loadStock();
    } catch (err) {
      showApiError(err, t);
    }
  };

  const showHistory = (item) => {
    setHistoryItem(item);
    setTab('history');
    loadHistory(item);
  };

  const filledCount = Object.values(countValues).filter((v) => parseQty(v) !== null).length;

  const submitCount = async () => {
    const entries = Object.entries(countValues)
      .map(([item_id, raw]) => ({ item_id, qty: parseQty(raw) }))
      .filter((e) => e.qty !== null);
    if (entries.some((e) => e.qty < 0)) {
      toast.error(t('stock.validation.qtyZero'));
      return;
    }
    if (!entries.length) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/stock/count`, {
        entries,
        note: t('stock.countNote'),
      });
      toast.success(t('stock.count.savedToast', { count: data.counted }));
      setCountValues({});
      setTab('stock');
      loadStock();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const visibleCountRows = useMemo(() => {
    const q = countQuery.trim().toLocaleLowerCase(locale);
    if (!q) return countRows;
    return countRows.filter((r) => [r.name, r.name_en, r.sku, r.brand, r.model, r.category]
      .some((v) => (v || '').toLocaleLowerCase(locale).includes(q)));
  }, [countRows, countQuery, locale]);

  const showValue = summary?.value_by_currency !== null;
  const values = summary?.value_by_currency || {};
  const valueText = Object.keys(values).length
    ? Object.entries(values).map(([cur, minor]) => formatMinor(minor, cur, locale)).join(' + ')
    : formatMinor(0, 'TRY', locale);

  const qtyClass = (row) => {
    if (row.stock_qty < 0) return 'text-red-600 font-semibold';
    if (row.is_low) return 'text-amber-700 font-semibold';
    return 'text-[#1A1A2E] font-semibold';
  };

  const movementTitle = movement ? t(`stock.movement.title.${movement.type}`) : '';

  // ------------------------------------------------------------ render
  return (
    <QuoteLayout>
      <div className="max-w-6xl mx-auto" data-testid="quote-stock-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('stock.title')}</h1>
            <p className="text-sm text-[#595959] mt-1">{t('stock.subtitle')}</p>
            {stockLimit !== null && (
              <p className="mt-1 text-xs text-[#475569]" data-testid="stock-plan-usage">
                {t('stock.planUsage', { count: entitlement?.usage?.stock_item_count ?? 0, limit: stockLimit })}{' '}
                <a href="/quote-upgrade" className="font-semibold text-[#2563EB] underline">{t('stock.planUnlimited')}</a>
              </p>
            )}
          </div>
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Button onClick={() => (barcodeLocked ? goUpgrade() : setScanning(true))} className="col-span-2 sm:col-span-1 bg-[#2563EB] hover:bg-[#1E4ED8] px-2 sm:px-4" data-testid="stock-scan-btn">
              <ScanLine className="w-4 h-4 mr-1.5 shrink-0" />
              <span className="truncate">{t('stock.scan.button')}</span>
              {barcodeLocked && <PlanLockBadge />}
            </Button>
            <Button variant="outline" onClick={() => setTab('count')} className="px-2 sm:px-4" data-testid="stock-count-btn">
              <ClipboardCheck className="w-4 h-4 mr-1.5 shrink-0" />
              <span className="truncate">{t('stock.countButton')}</span>
            </Button>
            <Button variant="outline" onClick={() => (barcodeLocked ? goUpgrade() : setLabelsOpen(true))} className="px-2 sm:px-4" data-testid="stock-labels-btn">
              <Printer className="w-4 h-4 mr-1.5 shrink-0" />
              <span className="truncate">{t('stock.labels.button')}</span>
              {barcodeLocked && <PlanLockBadge />}
            </Button>
          </div>
        </div>

        <div className={`grid ${showValue ? 'grid-cols-3' : 'grid-cols-2'} gap-2 sm:gap-3 mb-6`}>
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-3 sm:p-4 flex items-center gap-3">
            <Boxes className="hidden sm:block w-8 h-8 text-[#2563EB] shrink-0" />
            <div>
              <div className="text-[11px] sm:text-xs text-[#595959] leading-tight">{t('stock.summary.tracked')}</div>
              <div className="text-lg sm:text-xl font-bold text-[#1A1A2E]" data-testid="stock-tracked-count">{summary?.tracked_count ?? 0}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setTab('stock'); setLowOnly(true); }}
            className="bg-white rounded-2xl border border-[#E5E5E5] p-3 sm:p-4 flex items-center gap-3 text-left hover:border-amber-400"
            data-testid="stock-low-card"
          >
            <AlertTriangle className={`hidden sm:block w-8 h-8 shrink-0 ${summary?.low_count ? 'text-amber-600' : 'text-[#94A3B8]'}`} />
            <div>
              <div className="text-[11px] sm:text-xs text-[#595959] leading-tight">{t('stock.summary.low')}</div>
              <div className="text-lg sm:text-xl font-bold text-[#1A1A2E]">{summary?.low_count ?? 0}</div>
            </div>
          </button>
          {showValue && (
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-3 sm:p-4 flex items-center gap-3">
            <Wallet className="hidden sm:block w-8 h-8 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] sm:text-xs text-[#595959] leading-tight">{t('stock.summary.value')}</div>
              <div className="text-sm sm:text-xl font-bold text-[#1A1A2E] truncate" title={valueText}>{valueText}</div>
            </div>
          </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="stock" data-testid="stock-tab">{t('stock.tabs.stock')}</TabsTrigger>
            <TabsTrigger value="history" data-testid="stock-history-tab" onClick={() => setHistoryItem(null)}>{t('stock.tabs.history')}</TabsTrigger>
            <TabsTrigger value="count" data-testid="stock-count-tab">{t('stock.tabs.count')}</TabsTrigger>
          </TabsList>

          {/* ---------------------------------------------------- stock list */}
          <TabsContent value="stock">
            <form onSubmit={(e) => { e.preventDefault(); loadStock(); }} className="mb-4 flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('stock.searchPlaceholder')}
                  className="pl-9"
                  data-testid="stock-search-input"
                />
              </div>
              <Button type="submit" variant="outline">{t('common.search')}</Button>
              <Button
                type="button"
                variant={lowOnly ? 'default' : 'outline'}
                onClick={() => setLowOnly((v) => !v)}
                data-testid="stock-low-toggle"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                {t('stock.lowOnly')}
              </Button>
            </form>

            <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
              ) : items.length === 0 ? (
                <div className="p-10 text-center" data-testid="stock-empty">
                  <Boxes className="w-10 h-10 mx-auto text-[#94A3B8] mb-3" />
                  <p className="text-[#475569] mb-4">
                    {lowOnly || query ? t('stock.emptyFiltered') : t('stock.empty')}
                  </p>
                  {!lowOnly && !query && (
                    <Button onClick={() => setTab('count')} className="bg-[#2563EB] hover:bg-[#1E4ED8]">
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      {t('stock.startCount')}
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {items.map((row) => (
                    <li key={row.id} className="px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3" data-testid={`stock-row-${row.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1A1A2E] line-clamp-2 break-words">{name(row)}</div>
                        <div className="text-xs text-[#94A3B8] truncate">
                          {[row.brand, row.model, row.sku].filter(Boolean).join(' · ') || pickLocalizedField(row, 'category', lang)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={qtyClass(row)}>
                          {fmtQty(row.stock_qty)} <span className="text-xs font-normal text-[#595959]">{unit(row)}</span>
                        </div>
                        {row.min_stock !== null && row.min_stock !== undefined && (
                          <div className="text-[11px] text-[#94A3B8]">{t('stock.minShort', { value: fmtQty(row.min_stock) })}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                        <Button size="icon" variant="outline" className="h-9 w-9" title={t('stock.actions.in')}
                          onClick={() => openMovement(row, 'in')} data-testid={`stock-in-${row.id}`}>
                          <Plus className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-9 w-9" title={t('stock.actions.out')}
                          onClick={() => openMovement(row, 'out')} data-testid={`stock-out-${row.id}`}>
                          <Minus className="w-4 h-4 text-red-600" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-9 w-9" data-testid={`stock-more-${row.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openMovement(row, 'return')}>
                              <Undo2 className="w-4 h-4 mr-2" />{t('stock.actions.return')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setCorrecting(row); setCorrectQty(String(row.stock_qty)); }}>
                              <ClipboardCheck className="w-4 h-4 mr-2" />{t('stock.actions.correct')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => showHistory(row)}>
                              <History className="w-4 h-4 mr-2" />{t('stock.actions.history')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setSettingsItem(row); setMinStock(row.min_stock ?? ''); setBarcode(row.barcode || ''); }}>
                              <AlertTriangle className="w-4 h-4 mr-2" />{t('stock.actions.settings')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* ---------------------------------------------------- history */}
          <TabsContent value="history">
            {historyItem && (
              <div className="mb-3 flex items-center gap-2 text-sm">
                <span className="text-[#475569]">{t('stock.history.filteredBy', { name: name(historyItem) })}</span>
                <Button size="sm" variant="ghost" onClick={() => { setHistoryItem(null); loadHistory(null); }}>
                  {t('stock.history.showAll')}
                </Button>
              </div>
            )}
            <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
              {historyLoading ? (
                <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
              ) : history.length === 0 ? (
                <div className="p-10 text-center text-[#94A3B8]">{t('stock.history.empty')}</div>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {history.map((m) => (
                    <li key={m.id} className="px-4 py-3 flex items-center gap-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1A1A2E] truncate">{m.item_name}</div>
                        <div className="text-xs text-[#94A3B8] truncate">
                          {t(`stock.type.${m.type}`)}
                          {' · '}{new Date(m.created_at).toLocaleString(locale)}
                          {m.user_name ? ` · ${m.user_name}` : ''}
                          {m.note ? ` · ${m.note}` : ''}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={m.delta > 0 ? 'text-emerald-700 font-semibold' : m.delta < 0 ? 'text-red-600 font-semibold' : 'text-[#595959]'}>
                          {m.delta > 0 ? '+' : ''}{fmtQty(m.delta)}
                        </div>
                        <div className="text-[11px] text-[#94A3B8]">{t('stock.history.after', { value: fmtQty(m.qty_after) })}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* ---------------------------------------------------- count */}
          <TabsContent value="count">
            <p className="text-sm text-[#475569] mb-3">{t('stock.count.help')}</p>
            <div className="mb-4 flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={countQuery}
                  onChange={(e) => setCountQuery(e.target.value)}
                  placeholder={t('stock.searchPlaceholder')}
                  className="pl-9"
                  data-testid="stock-count-search"
                />
              </div>
              <Button
                onClick={submitCount}
                disabled={!filledCount || saving}
                className="bg-[#2563EB] hover:bg-[#1E4ED8]"
                data-testid="stock-count-save"
              >
                {t('stock.count.save', { count: filledCount })}
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
              {countLoading ? (
                <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
              ) : visibleCountRows.length === 0 ? (
                <div className="p-10 text-center text-[#94A3B8]">{t('stock.count.empty')}</div>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {visibleCountRows.map((row) => (
                    <li key={row.id} className="px-4 py-2.5 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1A1A2E] truncate">{name(row)}</div>
                        <div className="text-xs text-[#94A3B8] truncate">
                          {row.track_stock
                            ? t('stock.count.current', { value: fmtQty(row.stock_qty), unit: unit(row) })
                            : t('stock.count.notTracked')}
                        </div>
                      </div>
                      <Input
                        inputMode="decimal"
                        className="w-24 text-right"
                        placeholder={unit(row) || '0'}
                        value={countValues[row.id] ?? ''}
                        onChange={(e) => setCountValues((prev) => ({ ...prev, [row.id]: e.target.value }))}
                        data-testid={`stock-count-input-${row.id}`}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ------------------------------------------------------ movement dialog */}
      <Dialog open={!!movement} onOpenChange={(open) => !open && setMovement(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{movementTitle}</DialogTitle>
            {movement && (
              <DialogDescription>
                {name(movement.item)} · {t('stock.currentQty', { value: fmtQty(movement.item.stock_qty), unit: unit(movement.item) })}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('stock.movement.qty')}</Label>
              <Input
                autoFocus
                inputMode="decimal"
                value={movementQty}
                onChange={(e) => setMovementQty(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitMovement(); }}
                data-testid="stock-movement-qty"
              />
            </div>
            <div>
              <Label>{t('stock.movement.note')}</Label>
              <Textarea
                rows={2}
                value={movementNote}
                onChange={(e) => setMovementNote(e.target.value)}
                placeholder={t(`stock.movement.notePlaceholder.${movement?.type || 'in'}`)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovement(null)}>{t('common.cancel')}</Button>
            <Button onClick={submitMovement} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="stock-movement-save">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------ correction dialog */}
      <Dialog open={!!correcting} onOpenChange={(open) => !open && setCorrecting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('stock.correct.title')}</DialogTitle>
            {correcting && <DialogDescription>{name(correcting)} · {t('stock.correct.help')}</DialogDescription>}
          </DialogHeader>
          <div>
            <Label>{t('stock.correct.realQty')}</Label>
            <Input
              autoFocus
              inputMode="decimal"
              value={correctQty}
              onChange={(e) => setCorrectQty(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitCorrection(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrecting(null)}>{t('common.cancel')}</Button>
            <Button onClick={submitCorrection} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------ settings dialog */}
      <Dialog open={!!settingsItem} onOpenChange={(open) => !open && setSettingsItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('stock.settings.title')}</DialogTitle>
            {settingsItem && <DialogDescription>{name(settingsItem)}</DialogDescription>}
          </DialogHeader>
          <div>
            <Label>{t('stock.settings.minStock')}</Label>
            <Input
              inputMode="decimal"
              value={minStock}
              onChange={(e) => setMinStock(e.target.value)}
              placeholder={t('stock.settings.minStockPlaceholder')}
            />
            <p className="text-xs text-[#94A3B8] mt-1">{t('stock.settings.minStockHelp')}</p>
          </div>
          <div>
            <Label>{t('stock.settings.barcode')}{barcodeLocked && <PlanLockBadge />}</Label>
            <Input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder={t('stock.settings.barcodePlaceholder')} disabled={barcodeLocked} data-testid="stock-barcode-input" />
            <p className="text-xs text-[#94A3B8] mt-1">{barcodeLocked ? t('stock.settings.barcodeLocked') : t('stock.settings.barcodeHelp')}</p>
          </div>
          <DialogFooter className="sm:justify-between gap-2">
            <Button variant="ghost" className="text-red-600" onClick={() => stopTracking(settingsItem)}>
              {t('stock.settings.stopTracking')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsItem(null)}>{t('common.cancel')}</Button>
              <Button onClick={saveSettings} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]">{t('common.save')}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StockScanFlow open={scanning} onClose={() => setScanning(false)} onChanged={loadStock} />
      <StockLabelsDialog open={labelsOpen} onClose={() => setLabelsOpen(false)} />
    </QuoteLayout>
  );
}
